// God-tier Topbar — glassmorphic, information-dense, animated indicators
import { useServiceHealth, DEGRADATION_MATRIX } from '@/context/ServiceHealthContext'
import { useAuth } from '@/context/AuthContext'
import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useLocation } from 'react-router-dom'
import { Search } from 'lucide-react'

const SERVICE_LABELS: Record<string, string> = {
  gladia:         'TXN',
  anthropic:      'AI',
  telnyx:         'TEL',
  translationVps: 'TRL',
  resend:         'ML',
  wave:           'ACC',
}

function formatTime(date: Date, tz: string): string {
  return date.toLocaleTimeString('en-US', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
}

function LiveClock() {
  const [now, setNow] = useState(new Date())
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(id)
  }, [])

  return (
    <div className="dual-clock">
      <div className="clock-zone">
        <span className="clock-label">PKT</span>
        <span className="clock-time">{formatTime(now, 'Asia/Karachi')}</span>
      </div>
      <span style={{ color: 'var(--border-default)', fontSize: '0.6875rem', fontWeight: 300 }}>·</span>
      <div className="clock-zone">
        <span className="clock-label">EST</span>
        <span className="clock-time">{formatTime(now, 'America/New_York')}</span>
      </div>
    </div>
  )
}

const ROLE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  admin:      { bg: 'rgba(37,99,235,0.12)',   text: '#93C5FD', border: 'rgba(37,99,235,0.25)'   },
  supervisor: { bg: 'rgba(245,158,11,0.10)',  text: '#FCD34D', border: 'rgba(245,158,11,0.22)'  },
  agent:      { bg: 'rgba(255,255,255,0.05)', text: '#94A3B8', border: 'rgba(255,255,255,0.10)' },
}

const ROUTE_TITLES: Record<string, string> = {
  '/dashboard':   'Overview',
  '/leads':       'Lead Pipeline',
  '/agents':      'Agent Roster',
  '/war-room':    'Live Floor',
  '/reports':     'Payroll & Reports',
  '/settings':    'Settings',
  '/shifts':      'Shift Management',
  '/sms':         'SMS Inbox',
  '/calls':       'Call Dialer',
  '/sequences':   'Sequences',
  '/sales':       'Sales Analytics',
  '/leaderboard': 'Leaderboard',
  '/scripts':     'Script Library',
  '/hr':          'HR & Records',
  '/profile':     'My Profile',
}

export default function Topbar() {
  const { agent } = useAuth()
  const { health, isAnyDown } = useServiceHealth()
  const location = useLocation()

  const role = agent?.role ?? 'agent'
  const roleStyle = ROLE_COLORS[role] ?? ROLE_COLORS.agent
  const currentPageTitle = ROUTE_TITLES[location.pathname] ?? 'Command Centre'

  // Open cmdk on click
  const openCmdk = () => {
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))
  }

  return (
    <header className="topbar portal-topbar">
      {/* Left: logo + role pill */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', flexShrink: 0 }}>
        <img
          src="/callro_logo.png"
          alt="Callro"
          style={{ height: '1.375rem', width: 'auto', objectFit: 'contain', opacity: 0.95 }}
        />
        {agent?.role && (
          <span style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: '0.5625rem',
            fontWeight: 700,
            letterSpacing: '0.09em',
            textTransform: 'uppercase',
            color: roleStyle.text,
            background: roleStyle.bg,
            border: `1px solid ${roleStyle.border}`,
            borderRadius: '4px',
            padding: '0.125rem 0.3125rem',
            lineHeight: 1.5,
          }}>
            {role}
          </span>
        )}
      </div>

      {/* Breadcrumb Context */}
      <div style={{ display: 'flex', alignItems: 'center', marginLeft: '0.75rem' }}>
        <span style={{ color: 'var(--text-muted)', margin: '0 0.5rem' }}>/</span>
        <span style={{ 
          fontFamily: 'var(--font-display)', 
          fontSize: '0.875rem', 
          fontWeight: 600, 
          color: 'var(--text-primary)',
          letterSpacing: '-0.01em'
        }}>
          {currentPageTitle}
        </span>
      </div>

      <div style={{ flex: 1 }} />

      {/* Command Palette Trigger */}
      <button 
        onClick={openCmdk}
        style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-md)', padding: '0.375rem 0.75rem',
          color: 'var(--text-muted)', fontSize: '0.75rem', cursor: 'pointer',
          marginRight: '1rem', transition: 'all 150ms ease'
        }}
        className="hover:border-white/20 hover:text-white"
      >
        <Search size={14} />
        <span>Search or jump to...</span>
        <kbd style={{ 
          background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
          borderRadius: '4px', padding: '0 0.375rem', fontFamily: 'var(--font-mono)',
          fontSize: '0.6875rem', color: 'var(--text-secondary)'
        }}>⌘K</kbd>
      </button>

      <div style={{ width: '1px', height: '1.25rem', background: 'var(--border-subtle)', flexShrink: 0, marginRight: '1rem' }} />

      <LiveClock />

      <div style={{ width: '1px', height: '1.25rem', background: 'var(--border-subtle)', flexShrink: 0, marginLeft: '1rem' }} />

      {/* Service health — elegant animated rings */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
        {Object.entries(health).map(([key, status]) => {
          const isOnline  = status === 'online'
          const isOffline = status === 'offline'
          const dotColor  = isOnline ? '#22C55E' : isOffline ? '#EF4444' : '#334155'
          const label     = SERVICE_LABELS[key] ?? key.slice(0, 3).toUpperCase()

          return (
            <motion.div
              layout
              key={key}
              title={isOffline ? `${label}: ${DEGRADATION_MATRIX[key as keyof typeof DEGRADATION_MATRIX]}` : `${label}: ${status}`}
              style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'default' }}
            >
              {/* Premium indicator ring */}
              <div className="relative flex h-2 w-2 items-center justify-center">
                {isOnline && (
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60"></span>
                )}
                <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: dotColor }}></span>
              </div>
              <span style={{
                fontSize: '0.5625rem',
                fontFamily: "'JetBrains Mono', monospace",
                fontWeight: 600,
                color: isOffline ? '#FCA5A5' : 'var(--text-muted)',
                letterSpacing: '0.06em',
                transition: 'color 300ms ease'
              }}>
                {label}
              </span>
            </motion.div>
          )
        })}
      </div>

      <div style={{ width: '1px', height: '1.25rem', background: 'var(--border-subtle)', flexShrink: 0 }} />

      {/* Right: agent info */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', flexShrink: 0 }}>
        {isAnyDown && (
          <motion.span 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            style={{
              fontSize: '0.5625rem', fontWeight: 700, letterSpacing: '0.08em', color: '#FCA5A5',
              background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.22)',
              borderRadius: '4px', padding: '0.125rem 0.3125rem', textTransform: 'uppercase',
            }}
          >
            DEGRADED
          </motion.span>
        )}
        <span style={{
          fontSize: '0.8125rem', fontFamily: "'Inter', sans-serif", fontWeight: 500,
          color: 'var(--text-secondary)', letterSpacing: '-0.005em',
        }}>
          {agent?.name ?? '—'}
        </span>
      </div>
    </header>
  )
}
